import { useEffect, useState } from "react";
import { Copy, Check, Share2, Users, Award } from "lucide-react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";

export default function ReferralPanel() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    api.get("/referrals/me").then((r) => setData(r.data)).catch(() => {});
    api.get("/referrals/leaderboard").then((r) => setLeaderboard(r.data || [])).catch(() => {});
  }, []);

  if (!data) return null;

  const shareUrl = `${window.location.origin}/registro?ref=${data.code}`;

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* ignore */ }
  };

  const share = async () => {
    const payload = {
      title: "PK2 Scarlett",
      text: "Únete a PK2 Scarlett con mi código",
      url: shareUrl,
    };
    if (navigator.share) {
      try { await navigator.share(payload); return; } catch { /* user cancelled */ }
    }
    copy(shareUrl);
  };

  return (
    <div
      className="p-6"
      style={{ background: "var(--bg-surface)", border: "1px solid var(--border-accent)" }}
      data-testid="referral-panel"
    >
      <div className="overline mb-3">{t("referrals.title")}</div>
      <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>
        {t("referrals.subtitle", { days: data.vip_days_per_companion_referral })}
      </p>

      <div className="flex items-center gap-2 mb-6">
        <div
          className="flex-1 px-4 py-3 font-mono tracking-[0.4em] text-xl"
          style={{
            background: "rgba(255,0,51,0.07)",
            border: "1px solid var(--brand-primary)",
            color: "var(--brand-primary)",
            boxShadow: "inset 0 0 12px rgba(255,0,51,0.15)",
          }}
          data-testid="referral-code"
        >
          {data.code}
        </div>
        <button
          type="button"
          onClick={() => copy(data.code)}
          className="btn-ghost text-xs inline-flex items-center gap-2 px-4 py-3"
          data-testid="referral-copy"
          title={t("referrals.copy")}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? t("referrals.copied") : t("referrals.copy")}
        </button>
        <button
          type="button"
          onClick={share}
          className="btn-primary text-xs inline-flex items-center gap-2 px-4 py-3"
          data-testid="referral-share"
        >
          <Share2 size={14} /> {t("referrals.share_link")}
        </button>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-5">
        <Stat icon={<Users size={16} />} label={t("referrals.stats_referred")} value={data.total_referrals} />
        <Stat icon={<Award size={16} />} label={t("referrals.stats_companions")} value={data.companion_referrals} accent />
        <Stat label={t("referrals.stats_earned")} value={`${data.vip_days_earned}d`} accent />
      </div>

      {leaderboard.length > 0 && (
        <div className="pt-5" style={{ borderTop: "1px solid var(--border-subtle)" }}>
          <div className="overline mb-3">{t("referrals.leaderboard")}</div>
          <ol className="space-y-1" data-testid="referral-leaderboard">
            {leaderboard.map((u, i) => (
              <li key={i} className="flex items-center justify-between text-xs py-1">
                <span style={{ color: "var(--text-secondary)" }}>
                  <span className="mr-2 font-mono" style={{ color: "var(--text-muted)" }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {u.display_name}
                </span>
                <span style={{ color: "var(--brand-primary)" }}>{u.referral_count}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

function Stat({ icon, label, value, accent }) {
  return (
    <div
      className="p-3"
      style={{
        background: "var(--bg-elevated)",
        border: `1px solid ${accent ? "var(--vip-gold)" : "var(--border-subtle)"}`,
      }}
    >
      <div className="flex items-center gap-2 text-xs mb-1" style={{ color: "var(--text-muted)" }}>
        {icon} <span>{label}</span>
      </div>
      <div
        className="font-serif text-2xl"
        style={{ color: accent ? "var(--vip-gold)" : "var(--text-primary)" }}
      >
        {value}
      </div>
    </div>
  );
}
